import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Users, TrendingUp, Wallet, CreditCard, PiggyBank, HandCoins, ChevronDown, Apple, PlayCircle, Smartphone, Building2, Briefcase, UsersRound, TrendingDown, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Lightning Fast Transfers',
      description: 'Send money instantly to any bank account or wallet with zero delays'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Bank-Level Security',
      description: 'Your money is protected with military-grade encryption and biometric authentication'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Cooperative Savings (Esusu)',
      description: 'Join or create savings groups with friends, family, and communities'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Instant Micro-Loans',
      description: 'Access quick loans up to ₦5M with flexible repayment plans'
    },
    {
      icon: <Wallet className="w-8 h-8" />,
      title: 'Smart Budgeting',
      description: 'Track spending, set budgets, and achieve your financial goals'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'Virtual & Physical Cards',
      description: 'Get instant virtual cards or order physical cards for global use'
    }
  ];

  const whyBritedge = [
    { title: 'Lower Fees', desc: 'Save more with our competitive transaction fees' },
    { title: 'Better Rates', desc: 'Get the best interest rates on savings and loans' },
    { title: 'Faster Service', desc: 'No delays, no hassles - instant transactions' },
    { title: 'More Features', desc: 'Cooperatives, goal savings, budgeting tools & more' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://britedgeng.com/images/logo.png" 
                alt="Britedge" 
                className="h-10 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              <NavigationMenu>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-slate-700 hover:text-brand-blue">
                      <Building2 className="w-4 h-4 mr-1.5" />
                      Company
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="w-[220px] p-2">
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/about')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <div>
                                <div className="text-sm font-medium">About Us</div>
                                <p className="text-xs text-slate-600 mt-1">Our story and mission</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/team')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                              <div>
                                <div className="text-sm font-medium">Team</div>
                                <p className="text-xs text-slate-600 mt-1">Meet our people</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/career')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                              <div>
                                <div className="text-sm font-medium">Career</div>
                                <p className="text-xs text-slate-600 mt-1">Join our team</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-slate-700 hover:text-brand-blue">
                      <Briefcase className="w-4 h-4 mr-1.5" />
                      Business
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="w-[220px] p-2">
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/pos-terminals')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                              <div>
                                <div className="text-sm font-medium">POS Terminals</div>
                                <p className="text-xs text-slate-600 mt-1">Accept payments</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/atm-cards')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                              <div>
                                <div className="text-sm font-medium">ATM Cards</div>
                                <p className="text-xs text-slate-600 mt-1">Virtual & physical</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/business-account')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                              <div>
                                <div className="text-sm font-medium">Business Account</div>
                                <p className="text-xs text-slate-600 mt-1">For businesses</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/agent-network')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                              <div>
                                <div className="text-sm font-medium">Agent Network</div>
                                <p className="text-xs text-slate-600 mt-1">Become an agent</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-slate-700 hover:text-brand-blue">
                      <UsersRound className="w-4 h-4 mr-1.5" />
                      Cooperative
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="w-[220px] p-2">
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/server-circle')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                              <div>
                                <div className="text-sm font-medium">Server Circle</div>
                                <p className="text-xs text-slate-600 mt-1">Group & individual</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/coop-loans')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              <div>
                                <div className="text-sm font-medium">Loan</div>
                                <p className="text-xs text-slate-600 mt-1">Quick coop loans</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/edufuture')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                              <div>
                                <div className="text-sm font-medium">EduFuture</div>
                                <p className="text-xs text-slate-600 mt-1">Save for education</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="text-slate-700 hover:text-brand-blue">
                      <TrendingDown className="w-4 h-4 mr-1.5" />
                      Buy-Back
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="w-[220px] p-2">
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/buyback')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                              <div>
                                <div className="text-sm font-medium">Investment Plans</div>
                                <p className="text-xs text-slate-600 mt-1">3mo, 6mo, 1yr at 18%</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                        <li>
                          <NavigationMenuLink asChild>
                            <button onClick={() => navigate('/buyback#land')} className="w-full text-left flex items-center gap-3 select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-brand-blue">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                              <div>
                                <div className="text-sm font-medium">Land Investment</div>
                                <p className="text-xs text-slate-600 mt-1">Invest in real estate</p>
                              </div>
                            </button>
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <button onClick={() => navigate('/contact')} className="text-slate-700 hover:text-brand-blue px-4 py-2 text-sm font-medium flex items-center gap-1.5">
                      <Mail className="w-4 h-4" />
                      Contact
                    </button>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div className="flex items-center gap-4">
              {/* <Button 
                variant="ghost" 
                onClick={() => navigate('#')}
                data-testid="nav-login-btn"
                className="rounded-full"
              >
                Login
              </Button> */}
              <Button 
                onClick={() => navigate('/contact')}
                data-testid="nav-signup-btn"
                className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-6 shadow-lg hover:shadow-[#221a83]/25 transition-all active:scale-95"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-4 md:px-8 gradient-hero relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-indigo-50 text-brand-blue px-4 py-2 rounded-full text-sm font-medium border border-indigo-100">
                <Zap className="w-4 h-4" />
                <span>Finance at you call</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-extrabold font-heading tracking-tight text-slate-900 leading-[1.1]">
                The Financial Edge You Deserve
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600">
                Experience the next generation of fintech. Send money, pay bills, save smart, access loans, and join cooperative groups - all in one powerful app built for Africans.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/about')}
                  data-testid="hero-get-started-btn"
                  className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-[#221a83]/30 transition-all active:scale-95"
                >
                  About Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/business-account')}
                  data-testid="hero-demo-btn"
                  className="bg-white border-slate-200 hover:bg-slate-50 rounded-full px-8 py-6 text-lg font-medium transition-all"
                >
                  For Business
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-slate-200" style={{backgroundImage: `url(https://api.dicebear.com/7.x/avataaars/svg?seed=${i})`}} />
                  ))}
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">Thousands of Users</div>
                  <div className="text-slate-600">Trust Britedge daily</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl hover-lift">
                <img 
                  src="https://britedgeng.com/images/home1.png" 
                  alt="Lagos Skyline" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#221a83]/80 via-[#221a83]/20 to-transparent"></div>
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <p className="text-3xl font-bold font-heading mb-2">Built for Africa</p>
                  <p className="text-indigo-100">Trusted by thousands across Nigeria</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-3xl md:text-5xl font-bold font-heading tracking-tight text-slate-900">
              Everything You Need In One App
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Britedge brings together all essential financial services with superior features and unmatched user experience
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                data-testid={`feature-card-${idx}`}
                className="group relative overflow-hidden bg-white border border-slate-200 rounded-3xl p-8 hover:border-brand-blue/50 transition-all duration-300 hover-lift"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-indigo-50 text-brand-blue flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                    {feature.icon}
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold font-heading text-slate-900">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Britedge */}
      <section className="py-20 md:py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://britedgeng.com/images/home2.png" 
                alt="Community" 
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-5xl font-bold font-heading tracking-tight text-slate-900">
                  Why Choose Britedge
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  We've built a superior platform with features that truly matter to African users
                </p>
              </div>
              <div className="space-y-4">
                {whyBritedge.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-indigo-50 transition-colors">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-blue text-white flex items-center justify-center font-bold">
                      {idx + 1}
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                onClick={() => navigate('/server-circle')}
                data-testid="why-britedge-cta-btn"
                className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-[#221a83]/25 transition-all active:scale-95"
              >
                Experience The Difference
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Cooperative Feature Highlight */}
      <section className="py-20 md:py-32 px-4 md:px-8 bg-gradient-to-br from-[#221a83] to-[#1a1466] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                <HandCoins className="w-4 h-4" />
                <span>Unique to Britedge</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold font-heading tracking-tight">
                Cooperative Savings Made Easy
              </h2>
              <p className="text-lg text-indigo-100 leading-relaxed">
                Join existing or create custom savings groups. Manage contributions, track payouts, and build wealth together with your community.
              </p>
              <ul className="space-y-3">
                {['Create unlimited groups', 'Automated contributions', 'Transparent payout schedules', 'In-app communication'].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    </div>
                    <span className="text-indigo-50">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">Active Groups</h3>
                  <PiggyBank className="w-6 h-6" />
                </div>
                <div className="space-y-3">
                  {['Office Friends Esusu', 'Family Goal Saving', 'Market Women Ajo'].map((group, idx) => (
                    <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20"></div>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{group}</p>
                          <p className="text-xs text-indigo-200">₦{(50000 - idx * 10000).toLocaleString()} monthly</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-indigo-200">Next payout</p>
                          <p className="font-bold text-sm">Jan {10 + idx * 5}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section className="py-20 md:py-32 px-4 md:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-5xl font-bold font-heading tracking-tight">
                  Download Britedge
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed">
                  Download our mobile app and take control of your finances anywhere, anytime. Available on iOS and Android.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => window.location.href = 'https://apps.apple.com/app/britedge/id6758465105'}
                  data-testid="download-ios-btn"
                  className="flex items-center gap-3 bg-white text-slate-900 hover:bg-slate-100 rounded-2xl px-6 py-4 font-semibold transition-all active:scale-95 group"
                >
                  <Apple className="w-8 h-8" />
                  <div className="text-left">
                    <p className="text-xs">Download on the</p>
                    <p className="text-lg font-bold">App Store</p>
                  </div>
                </button>
                
                <button 
                  onClick={() => window.location.href = 'https://play.google.com/store/apps/details?id=com.britholdings.britedge'}
                  data-testid="download-android-btn"
                  className="flex items-center gap-3 bg-white text-slate-900 hover:bg-slate-100 rounded-2xl px-6 py-4 font-semibold transition-all active:scale-95 group"
                >
                  <PlayCircle className="w-8 h-8" />
                  <div className="text-left">
                    <p className="text-xs">Get it on</p>
                    <p className="text-lg font-bold">Google Play</p>
                  </div>
                </button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-slate-400">4.8/5 from 12,000+ reviews</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1719865400276-d1e7bde7e9c1?q=80&w=2070&auto=format&fit=crop" 
                  alt="Mobile App Preview" 
                  className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent rounded-3xl"></div>
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20">
                    <Smartphone className="w-10 h-10 text-white" />
                    <div>
                      <p className="font-bold text-white">Mobile First</p>
                      <p className="text-sm text-slate-300">Optimized for your phone</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold font-heading tracking-tight text-slate-900">
            Do You Need A POS For Your Business?
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            Join thousands of Nigerians already using Britedge POS to manager their business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button 
              onClick={() => navigate('/contact')}
              data-testid="final-cta-btn"
              className="bg-brand-blue text-white hover:bg-[#1a1466] rounded-full px-10 py-7 text-xl font-semibold shadow-2xl hover:shadow-[#221a83]/30 transition-all active:scale-95"
            >
              Contact Us
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </div>
          <p className="text-sm text-slate-500">No credit card required • Free forever • Get started in 2 minutes</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8 mb-12">
            <div className="md:col-span-2 space-y-4">
              <img 
                src="https://britedgeng.com/images/logo.png" 
                alt="Britedge" 
                className="h-10 w-auto brightness-0 invert"
              />
              <p className="text-sm text-slate-400 leading-relaxed">
                The Financial Edge You Deserve. Nigeria's leading fintech platform for transfers, savings, loans, and cooperative banking.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121L7.884 13.53l-2.89-.903c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-slate-800 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => navigate('/team')} className="hover:text-white transition-colors">Team</button></li>
                <li><button onClick={() => navigate('/career')} className="hover:text-white transition-colors">Careers</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Business</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/pos-terminals')} className="hover:text-white transition-colors">POS Terminals</button></li>
                <li><button onClick={() => navigate('/agent-network')} className="hover:text-white transition-colors">Agent Network</button></li>
                <li><button onClick={() => navigate('/business-account')} className="hover:text-white transition-colors">Business Account</button></li>
                <li><button onClick={() => navigate('/atm-cards')} className="hover:text-white transition-colors">ATM Cards</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/complaints')} className="hover:text-white transition-colors">Complaints</button></li>
                <li><button onClick={() => navigate('/loan-privacy')} className="hover:text-white transition-colors">Loan Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms of Service</button></li>
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy Policy</button></li>
              </ul>
            </div>
          </div>

          {/* Compliance Logos */}
          <div className="border-t border-slate-800 pt-8 mb-8">
            <p className="text-sm text-slate-400 mb-6 text-center">Regulated and Certified By:</p>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              {/* CBN - Central Bank of Nigeria */}
              {/* <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 min-w-[140px] justify-center">
                <svg className="w-8 h-8 text-green-700" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/>
                </svg>
                <div className="text-left">
                  <p className="text-xs font-bold text-slate-900">CBN</p>
                  <p className="text-[10px] text-slate-600">Licensed</p>
                </div>
              </div> */}
              
              {/* NDIC - Nigeria Deposit Insurance Corporation */}
              {/* <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 min-w-[140px] justify-center">
                <svg className="w-8 h-8 text-blue-700" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                </svg>
                <div className="text-left">
                  <p className="text-xs font-bold text-slate-900">NDIC</p>
                  <p className="text-[10px] text-slate-600">Insured</p>
                </div>
              </div> */}
              
              {/* PCI DSS */}
              <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 min-w-[140px] justify-center">
                <svg className="w-8 h-8 text-indigo-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                </svg>
                <div className="text-left">
                  <p className="text-xs font-bold text-slate-900">PCI DSS</p>
                  <p className="text-[10px] text-slate-600">Compliant</p>
                </div>
              </div>
              
              {/* NDPC - Nigeria Data Protection Commission */}
              <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 min-w-[140px] justify-center">
                <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                </svg>
                <div className="text-left">
                  <p className="text-xs font-bold text-slate-900">NDPR</p>
                  <p className="text-[10px] text-slate-600">Compliant</p>
                </div>
              </div>
              
              {/* GDPR */}
              <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 min-w-[140px] justify-center">
                <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <div className="text-left">
                  <p className="text-xs font-bold text-slate-900">GDPR</p>
                  <p className="text-[10px] text-slate-600">Protected</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            <p>&copy; 2026 Britedge Financial Services. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
